import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';

import { axios } from 'utils/axios';
import { AxiosError } from 'axios';
import { EducationStatus, UserExamination } from '@novaheal/types';

export type EducationProgressionPromptDto = {
  id: string;
  currentStage: EducationStatus;
  suggestedStage: EducationStatus;
  isFollowUp: boolean;

  // Current user data for pre-filling inline form only
  currentExaminationDate: UserExamination | null;
  currentExaminationDateUnknown?: boolean;
};

const getEducationProgressionPrompt = async (): Promise<EducationProgressionPromptDto | null> => {
  const { data } = await axios.get<EducationProgressionPromptDto>(`/education-progression/prompt`);
  return data;
};

type UseGetEducationProgressionPromptOptions = {
  enabled?: boolean;
};

/** Loads a cached summary only; does not trigger AI generation. */
export const useGetEducationProgressionPrompt = (
  options: UseGetEducationProgressionPromptOptions = {}
): UseQueryResult<EducationProgressionPromptDto | null, unknown> => {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['education-progression-prompt'],
    queryFn: () => getEducationProgressionPrompt(),
    enabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

type ConfirmEducationProgressionPromptDto = {
  id: string;
};

const confirmEducationProgressionPrompt = async ({ id }: { id: string }): Promise<void> => {
  await axios.patch<void>(`/education-progression/prompt/confirm/${id}`);
};

export const useConfirmEducationProgressionPrompt = (): UseMutationResult<
  void,
  AxiosError,
  ConfirmEducationProgressionPromptDto,
  string
> => {
  return useMutation({
    mutationFn: confirmEducationProgressionPrompt,
  });
};

type DismissEducationProgressionPromptDto = {
  id: string;
};

const dismissEducationProgressionPrompt = async ({ id }: { id: string }): Promise<void> => {
  await axios.patch<void>(`/education-progression/prompt/dismiss/${id}`);
};

export const useDismissEducationProgressionPrompt = (): UseMutationResult<
  void,
  AxiosError,
  DismissEducationProgressionPromptDto,
  string
> => {
  return useMutation({
    mutationFn: dismissEducationProgressionPrompt,
  });
};

export type EducationProgressionAdjustmentDto = {
  promptId: string;
  educationStatus: EducationStatus;
  examination?: UserExamination | null;
  examinationDateUnknown?: boolean;
};

const adjustEducationProgressionPrompt = async ({
  promptId,
  educationStatus,
  examination,
  examinationDateUnknown,
}: EducationProgressionAdjustmentDto): Promise<void> => {
  await axios.patch<void>(`/education-progression/prompt/adjust/${promptId}`, {
    educationStatus,
    examination,
    examinationDateUnknown,
  });
};

export const useAdjustEducationProgressionPrompt = (): UseMutationResult<
  void,
  AxiosError,
  EducationProgressionAdjustmentDto,
  string
> => {
  return useMutation({
    mutationFn: adjustEducationProgressionPrompt,
  });
};
